import { useCallback, useEffect, useMemo, useState } from "react";
import { FaTrash, FaUndo } from "react-icons/fa";
import { Redirect, useHistory, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import InstallationMaskEditor from "./installation/InstallationMaskEditor";
import InstallationSubpageLayout from "./installation/InstallationSubpageLayout";
import {
  areCropPolygonsEqual,
  formatCropMaskStatus,
  getCameraForSide,
  getPrimaryCropPolygon,
  getRecentRawImageEntry,
  getRecentRawImageUrl,
  isInstallationSide,
  INSTALLATION_DETAIL_STALE_KEY,
  INSTALLATION_LIST_STALE_KEY,
  resolveCropImageDimensions,
} from "./installation/installationConfigUtils";
import { saveCropMaskConfig } from "./installation/installationApi";
import { useInstallationCamera } from "./installation/useInstallationCamera";
import { useInstallationSideLabels } from "./installation/installationSideUtils";
import { useInstallationImageRotation } from "./installation/useInstallationImageRotation";

const AppInstallationSideMask = ({ setCookies, saveUser }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { id, side } = useParams();
  const { getMaskPageTitle } = useInstallationSideLabels();
  const [saving, setSaving] = useState(false);
  const [savedPoints, setSavedPoints] = useState([]);
  const [draftPoints, setDraftPoints] = useState([]);

  const {
    loading,
    leftCamera,
    rightCamera,
    configData,
    setConfigData,
    cycleEntries,
    api,
    token,
    reload,
  } = useInstallationCamera(id);

  const validSide = isInstallationSide(side) ? side : "left";
  const pageTitle = getMaskPageTitle(validSide);
  const camera = getCameraForSide(leftCamera, rightCamera, validSide);
  const recentCycleEntry = useMemo(
    () => getRecentRawImageEntry(cycleEntries),
    [cycleEntries],
  );
  const imageUrl = useMemo(
    () => getRecentRawImageUrl(id, cycleEntries, validSide),
    [id, cycleEntries, validSide],
  );
  const dimensions = useMemo(
    () => resolveCropImageDimensions(recentCycleEntry),
    [recentCycleEntry],
  );
  const { targetAngle, previewDelta, isInSync } = useInstallationImageRotation({
    routeCameraId: id,
    side: validSide,
    cycleEntries,
    camera,
  });
  const maskStatus = formatCropMaskStatus(t, configData, validSide);
  const isDirty = !areCropPolygonsEqual(draftPoints, savedPoints);
  const canSave = draftPoints.length >= 3;

  useEffect(() => {
    const loadedPoints = getPrimaryCropPolygon(configData, validSide);
    setSavedPoints(loadedPoints);
    setDraftPoints(loadedPoints);
  }, [configData, validSide]);

  const persistMask = useCallback(
    async (polygons) => {
      setSaving(true);
      try {
        const { output } = await saveCropMaskConfig(
          api,
          token,
          id,
          configData,
          validSide,
          polygons,
        );
        setConfigData(output);
        const nextPoints = polygons[0]
          ? polygons[0].map(([x, y]) => [x, y])
          : [];
        setSavedPoints(nextPoints);
        setDraftPoints(nextPoints);
        sessionStorage.setItem(INSTALLATION_LIST_STALE_KEY, "1");
        sessionStorage.setItem(INSTALLATION_DETAIL_STALE_KEY, "1");
        toast.success(t("installation_saved"));
        await reload({ silent: true });
        return true;
      } catch {
        toast.error(t("installation_save_failed"));
        return false;
      } finally {
        setSaving(false);
      }
    },
    [api, token, id, configData, validSide, setConfigData, t, reload],
  );

  const dismiss = () => {
    setDraftPoints(savedPoints);
    history.goBack();
  };

  const handleSave = async () => {
    if (!canSave || !isDirty || saving) {
      if (!canSave && isDirty) {
        toast.error(t("installation_mask_min_points"));
      }
      return;
    }
    const saved = await persistMask([draftPoints]);
    if (saved) {
      history.goBack();
    }
  };

  const clearMask = () => {
    setDraftPoints([]);
  };

  const undoLastPoint = () => {
    setDraftPoints((current) => current.slice(0, -1));
  };

  if (!isInstallationSide(side)) {
    return <Redirect to={`/app/installationdetails/${id}`} />;
  }

  const headerAction = isDirty ? (
    <button
      type="button"
      className="installation-subpage-nav-button installation-subpage-nav-button-save"
      onClick={() => void handleSave()}
      disabled={saving || !canSave}
    >
      {saving ? t("installation_saving") : t("installation_save")}
    </button>
  ) : null;

  return (
    <InstallationSubpageLayout
      pageTitle={pageTitle}
      onBack={dismiss}
      setCookies={setCookies}
      saveUser={saveUser}
      headerAction={headerAction}
    >
      {loading && (
        <div className="installation-detail-state">
          <div className="installation-detail-spinner" aria-hidden />
          <p>{t("installation_loading")}</p>
        </div>
      )}

      {!loading && (
        <>
          <section className="installation-subpage-hero-metric installation-battery-hero">
            <span className="installation-subpage-hero-metric-eyebrow">
              {t("installation_mask_status_eyebrow")}
            </span>
            <p className="installation-subpage-hero-metric-value">
              {maskStatus}
            </p>
            <p className="installation-subpage-hero-metric-hint">
              {t("installation_crop_zone_card_desc")}
            </p>
          </section>

          {!isInSync && (
            <p className="installation-rotation-notice" role="status">
              {t("installation_mask_target_preview_notice", {
                angle: `${targetAngle}°`,
              })}
            </p>
          )}

          <div className="installation-subpage-card installation-subpage-card-flush installation-mask-editor-card">
            {imageUrl ? (
              <InstallationMaskEditor
                imageUrl={imageUrl}
                dimensions={dimensions}
                rotationAngle={targetAngle}
                imagePreviewRotation={previewDelta}
                draftPoints={draftPoints}
                onDraftPointsChange={setDraftPoints}
                disabled={saving}
              />
            ) : (
              <p className="installation-subpage-preview-empty">
                {t("installation_no_recent_image")}
              </p>
            )}
          </div>

          <p className="installation-crop-zone-draw-hint">
            {t("installation_mask_draw_hint")}
          </p>

          <div className="installation-mask-toolbar">
            <button
              type="button"
              className="installation-mask-toolbar-button"
              onClick={undoLastPoint}
              disabled={saving || draftPoints.length === 0}
            >
              <FaUndo aria-hidden />
              {t("installation_mask_undo_point")}
            </button>
            <button
              type="button"
              className="installation-mask-toolbar-button installation-mask-toolbar-button-danger"
              onClick={clearMask}
              disabled={saving || draftPoints.length === 0}
            >
              <FaTrash aria-hidden />
              {t("installation_mask_crop_clear_action")}
            </button>
          </div>

          <p className="installation-subpage-footnote">
            {t("installation_mask_points_count", { count: draftPoints.length })}
          </p>
        </>
      )}
    </InstallationSubpageLayout>
  );
};

export default AppInstallationSideMask;
